import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';
import type { AxisChartAPIState } from '@philips-internal/oswald-ui-components';
import { AutocompleteAPI, ChartAlignment, ChartDynamicRenderType, ComposedChartAPI, FetchAPI, TableAPI } from '@philips-internal/oswald-ui-components';
import { AxisChartAPI } from '@philips-internal/oswald-ui-components/lib/components/charts/axis-chart/axis-chart-api';
import Controller, { OnAfterFetch, OnChange, OnClick, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, ClickEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { isEmptyArrayOrObject, isMobileResolution } from '@philips-internal/oswald-ui-utils';

import { FragmentNames, OpenedOsAliases } from '../../../codegen';
import { Simple } from '../../../interfaces';
import { KpiGraphic } from '../../../interfaces/kpi-graphic';
import { FilterService } from '../../../services';
import { ChartFilterService } from '../../../services/chart-filter-service';
import { Utils } from '../../../utils';
import { Item } from '../item-interface';

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
@Controller({ fragment: FragmentNames.OPENED_OS })
export default class OpenedOsController {
  renderCustomDots = false;

  @Inject(OpenedOsAliases.MAIN_COMPOSED_CHART)
  composedChart!: ComposedChartAPI;

  @Inject(OpenedOsAliases.MAIN_COMPOSED_CHART_DEFEITO)
  composedChartDefeito!: ComposedChartAPI;

  @Inject(OpenedOsAliases.MAIN_COMPOSED_CHART_DUVIDA)
  composedChartDuvida!: ComposedChartAPI;

  @Inject(OpenedOsAliases.MAIN_COMPOSED_CHART_SOLICITACAO)
  composedChartSolicitacao!: ComposedChartAPI;

  @Inject(OpenedOsAliases.MAIN_COMPOSED_CHART_SUGESTAO)
  composedChartSugestao!: ComposedChartAPI;

  @Inject(OpenedOsAliases.TABLE_MONTH)
  tableMonth!: TableAPI;

  @Inject(OpenedOsAliases.TABLE_MONTH_DEFEITO)
  tableMonthDefeito!: TableAPI;

  @Inject(OpenedOsAliases.TABLE_MONTH_DUVIDA)
  tableMonthDuvida!: TableAPI;

  @Inject(OpenedOsAliases.TABLE_MONTH_SOLICITACAO)
  tableMonthSolicitacao!: TableAPI;

  @Inject(OpenedOsAliases.TABLE_MONTH_SUGESTAO)
  tableMonthSugestao!: TableAPI;

  @Inject(OpenedOsAliases.FETCH_MONTH)
  fetchMonth!: FetchAPI;

  @Inject(OpenedOsAliases.FETCH_MONTH_DEFEITO)
  fetchClassificationDefeito!: FetchAPI;

  @Inject(OpenedOsAliases.FETCH_MONTH_DUVIDA)
  fetchClassificationDuvida!: FetchAPI;

  @Inject(OpenedOsAliases.FETCH_MONTH_SOLICITACAO)
  fetchClassificationSolicitacao!: FetchAPI;

  @Inject(OpenedOsAliases.FETCH_MONTH_SUGESTAO)
  fetchClassificationSugestao!: FetchAPI;

  @Inject(OpenedOsAliases.AUTOCOMPLETE_YEAR)
  autocompleteYear!: AutocompleteAPI;

  @Inject(OpenedOsAliases.AUTOCOMPLETE_MONTH)
  autocompleteMonth!: AutocompleteAPI;

  graphics: KpiGraphic[] = [];
  yearValue: string | null = null;
  monthValue: number[] | null = null;
  classificationValue: string | null = null;
  timeCourseValue: string | null = null;

  @OnAfterFetch(OpenedOsAliases.FETCH_YEAR)
  onAfterFetchYear(): void {
    FilterService.onAfterFetchAutocompleteYear(this.autocompleteYear);
  }

  @OnMount(OpenedOsAliases.AUTOCOMPLETE_MONTH)
  onMountAutocompleteMonth(): void {
    this.autocompleteMonth.setData(FilterService.mountFieldMonth());
  }

  @OnChange(OpenedOsAliases.AUTOCOMPLETE_YEAR)
  onChangeAutocompleteYear(event: ChangeEvent<{ value: string }>): void {
    this.yearValue = event.value ? event.value.value : null;
  }

  @OnChange(OpenedOsAliases.AUTOCOMPLETE_MONTH)
  onChangeAutocompleteMonth(event: ChangeEvent<Item[]>): void {
    this.monthValue = event.value && Array.isArray(event.value) ? event.value.map(data => data.value) : null;
  }

  @OnSubmit(OpenedOsAliases.FILTER_0)
  onSubmitFilter(): void{
    this.executeSearch();
  }

  @OnDynamicContentRender(OpenedOsAliases.TABLE_MONTH)
  onRenderTableMonth(event: RenderEvent): void {
    switch (event.entry) {
      case 'labelOSOpenedVo':
        event.render((data: Simple): ReactElement => {
          const label = data.labelOSOpenedVo ?? data.label;
          if (label && label === 'Total') {
            return Utils.renderTableText('text.total');
          }
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);
          return Utils.renderTableText(`${String(monthNames[date.getMonth()])}-${String(date.getFullYear())}`);
        });
        break;
    }
  }

  @OnDynamicContentRender(OpenedOsAliases.TABLE_MONTH_DEFEITO)
  onRenderTableDefeito(event: RenderEvent): void {
    switch (event.entry) {
      case 'labelClassificationVo':
        event.render((data: Simple): ReactElement => {
          const label = data.labelClassificationVo ?? data.label;
          if (label && label === 'Total') {
            return Utils.renderTableText('text.total');
          }
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);
          return Utils.renderTableText(`${String(monthNames[date.getMonth()])}-${String(date.getFullYear())}`);
        });
        break;
    }
  }

  @OnDynamicContentRender(OpenedOsAliases.TABLE_MONTH_DUVIDA)
  onRenderTableDuvida(event: RenderEvent): void {
    switch (event.entry) {
      case 'labelClassificationVo':
        event.render((data: Simple): ReactElement => {
          const label = data.labelClassificationVo ?? data.label;
          if (label && label === 'Total') {
            return Utils.renderTableText('text.total');
          }
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);
          return Utils.renderTableText(`${String(monthNames[date.getMonth()])}-${String(date.getFullYear())}`);
        });
        break;
    }
  }

  @OnDynamicContentRender(OpenedOsAliases.TABLE_MONTH_SOLICITACAO)
  onRenderTableSolicitacao(event: RenderEvent): void {
    switch (event.entry) {
      case 'labelClassificationVo':
        event.render((data: Simple): ReactElement => {
          const label = data.labelClassificationVo ?? data.label;
          if (label && label === 'Total') {
            return Utils.renderTableText('text.total');
          }
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);
          return Utils.renderTableText(`${String(monthNames[date.getMonth()])}-${String(date.getFullYear())}`);
        });
        break;
    }
  }

  @OnDynamicContentRender(OpenedOsAliases.TABLE_MONTH_SUGESTAO)
  onRenderTableSugestao(event: RenderEvent): void {
    switch (event.entry) {
      case 'labelClassificationVo':
        event.render((data: Simple): ReactElement => {
          const label = data.labelClassificationVo ?? data.label;
          if (label && label === 'Total') {
            return Utils.renderTableText('text.total');
          }
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);
          return Utils.renderTableText(`${String(monthNames[date.getMonth()])}-${String(date.getFullYear())}`);
        });
        break;
    }
  }

  @OnAfterFetch(OpenedOsAliases.FETCH_MONTH)
  onAfterFetchMonth(event: FetchEvent): void {
    const data = event.data as Array<any>;
    this.setDataGraphics(data);
    if (!data || isEmptyArrayOrObject(this.tableMonth.getData())) {
      return;
    }
    this.tableMonth.selectRow(this.tableMonth.getData()[0].label as string);
  }

  @OnAfterFetch(OpenedOsAliases.FETCH_MONTH_DEFEITO)
  onAfterFetchMonthDefeito(event: FetchEvent): void {
    const data = event.data as Array<any>;
    this.setDataGraphicsDefeito(data);

    if (!data || isEmptyArrayOrObject(this.tableMonthDefeito.getData())) {
      return;
    }
    this.tableMonthDefeito.selectRow(this.tableMonthDefeito.getData()[0].label as string);
  }

  @OnAfterFetch(OpenedOsAliases.FETCH_MONTH_DUVIDA)
  onAfterFetchMonthDuvida(event: FetchEvent): void {
    const data = event.data as Array<any>;
    this.setDataGraphicsDuvida(data);

    if (!data || isEmptyArrayOrObject(this.tableMonthDuvida.getData())) {
      return;
    }
    this.tableMonthDuvida.selectRow(this.tableMonthDuvida.getData()[0].label as string);
  }

  @OnAfterFetch(OpenedOsAliases.FETCH_MONTH_SOLICITACAO)
  onAfterFetchMonthSolicitacao(event: FetchEvent): void {
    const data = event.data as Array<any>;
    this.setDataGraphicsSolicitacao(data);

    if (!data || isEmptyArrayOrObject(this.tableMonthSolicitacao.getData())) {
      return;
    }
    this.tableMonthSolicitacao.selectRow(this.tableMonthSolicitacao.getData()[0].label as string);
  }

  @OnAfterFetch(OpenedOsAliases.FETCH_MONTH_SUGESTAO)
  onAfterFetchMonthSugestao(event: FetchEvent): void {
    const data = event.data as Array<any>;
    this.setDataGraphicsSugestao(data);

    if (!data || isEmptyArrayOrObject(this.tableMonthSugestao.getData())) {
      return;
    }
    this.tableMonthSugestao.selectRow(this.tableMonthSugestao.getData()[0].label as string);
  }

  executeSearch(): void {
    ChartFilterService.executeSearch({
      valueYear: this.yearValue,
      valueMonth: this.monthValue,
      valueClassification: this.classificationValue,
      valueTimeCourse: this.timeCourseValue,
      fetchs: [
        this.fetchMonth,
        this.fetchClassificationDefeito,
        this.fetchClassificationDuvida,
        this.fetchClassificationSolicitacao,
        this.fetchClassificationSugestao,
      ],
      onExecute: () => {
        this.tableMonth.unselectAll();
        this.tableMonthDefeito.unselectAll();
        this.tableMonthDuvida.unselectAll;
        this.tableMonthSolicitacao;
        this.tableMonthSugestao;
      },
    });
  }

  // chart controller - Ordens de Serviço Encerradas *****************************************************************************************************************

  setDataGraphics(data: any[]): void {
    const kpiGraphics: KpiGraphic[] = [];

    if (data) {
      data.forEach((kpi: any) => {
        if (kpi.labelOSOpenedVo !== 'Total') {
          const label = String(kpi.labelOSOpenedVo);
          const amount = String(kpi.amountOSOpenedVo).includes('/') ? String(kpi.amountOSOpenedVo).split('/')[0] : kpi.amountOSOpenedVo;
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);

          kpiGraphics.push({
            date: `${String(monthNames[date.getMonth()])}`,
            OS_Total: Number(amount),
            // Tendency: 0, <--Descomentar quando o cálculo da tendencia estiver implementado
          });
        }
      });
    }

    this.graphics = kpiGraphics;
    this.composedChart.setData(this.graphics);
  }

  @OnMount(OpenedOsAliases.MAIN_COMPOSED_CHART)
  onChartMount(event: MountEvent): void {
    const chart = event.target as AxisChartAPI<AxisChartAPIState>;

    chart.setData(this.graphics);
    chart.setLegends({ verticalAlign: ChartAlignment.BOTTOM });
    chart.setXAxis({ dataKey: 'date', height: 50, interval: 'preserveStartEnd' });
    chart.setYAxis({ hide: isMobileResolution(), interval: 'preserveStartEnd', label: 'text.oss-total' });
  }

  @OnClick(OpenedOsAliases.MAIN_COMPOSED_CHART)
  onChartClick(event: ClickEvent): void {
    console.log('Clicked', event);
  }

  @OnDynamicContentRender(OpenedOsAliases.MAIN_COMPOSED_CHART)
  onLineChartDynamicContentRender(event: RenderEvent): void {
    const { render, type } = event;

    if (this.renderCustomDots && type === ChartDynamicRenderType.ACTIVE_DOT) {
      render((dotProps: Record<string, any>) => {
        const { cx, cy, fill } = dotProps;

        return (
          <svg fill={fill} height={20} viewBox="0 0 1024 1024" width={20} x={cx - 10} y={cy - 10}>
            <path d="M512 1009.984c-274.912 0-497.76-222.848-497.76-497.76s222.848-497.76 497.76-497.76c274.912 0 497.76 222.848 497.76 497.76s-222.848 497.76-497.76 497.76zM340.768 295.936c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM686.176 296.704c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM772.928 555.392c-18.752-8.864-40.928-0.576-49.632 18.528-40.224 88.576-120.256 143.552-208.832 143.552-85.952 0-164.864-52.64-205.952-137.376-9.184-18.912-31.648-26.592-50.08-17.28-18.464 9.408-21.216 21.472-15.936 32.64 52.8 111.424 155.232 186.784 269.76 186.784 117.984 0 217.12-70.944 269.76-186.784 8.672-19.136 9.568-31.2-9.12-40.096z" />
          </svg>
        );
      });
    }
  }

  // chart controller - Ordens de Serviço Defeito *****************************************************************************************************************

  setDataGraphicsDefeito(data: any[]): void {
    const kpiGraphics: KpiGraphic[] = [];

    if (data) {
      data.forEach((kpi: any) => {
        if (kpi.labelClassificationVo !== 'Total') {
          const label = String(kpi.labelClassificationVo);
          const amount = String(kpi.amountClassificationVo).includes('/') ? String(kpi.amountClassificationVo).split('/')[0] : kpi.amountClassificationVo;
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);

          kpiGraphics.push({
            date: `${String(monthNames[date.getMonth()])}`,
            OS_Total: Number(amount),
            // Tendency: 0, <--Descomentar quando o cálculo da tendencia estiver implementado
          });
        }
      });
    }

    this.graphics = kpiGraphics;
    this.composedChartDefeito.setData(this.graphics);
  }

  @OnMount(OpenedOsAliases.MAIN_COMPOSED_CHART_DEFEITO)
  onChartMountDefeito(event: MountEvent): void {
    const chart = event.target as AxisChartAPI<AxisChartAPIState>;

    chart.setData(this.graphics);
    chart.setLegends({ verticalAlign: ChartAlignment.BOTTOM });
    chart.setXAxis({ dataKey: 'date', height: 50, interval: 'preserveStartEnd' });
    chart.setYAxis({ hide: isMobileResolution(), interval: 'preserveStartEnd', label: 'text.os-defect' });
  }

  @OnClick(OpenedOsAliases.MAIN_COMPOSED_CHART_DEFEITO)
  onChartClickDefeito(event: ClickEvent): void {
    console.log('Clicked', event);
  }

  @OnDynamicContentRender(OpenedOsAliases.MAIN_COMPOSED_CHART_DEFEITO)
  onLineChartDynamicContentRenderDefeito(event: RenderEvent): void {
    const { render, type } = event;

    if (this.renderCustomDots && type === ChartDynamicRenderType.ACTIVE_DOT) {
      render((dotProps: Record<string, any>) => {
        const { cx, cy, fill } = dotProps;

        return (
          <svg fill={fill} height={20} viewBox="0 0 1024 1024" width={20} x={cx - 10} y={cy - 10}>
            <path d="M512 1009.984c-274.912 0-497.76-222.848-497.76-497.76s222.848-497.76 497.76-497.76c274.912 0 497.76 222.848 497.76 497.76s-222.848 497.76-497.76 497.76zM340.768 295.936c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM686.176 296.704c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM772.928 555.392c-18.752-8.864-40.928-0.576-49.632 18.528-40.224 88.576-120.256 143.552-208.832 143.552-85.952 0-164.864-52.64-205.952-137.376-9.184-18.912-31.648-26.592-50.08-17.28-18.464 9.408-21.216 21.472-15.936 32.64 52.8 111.424 155.232 186.784 269.76 186.784 117.984 0 217.12-70.944 269.76-186.784 8.672-19.136 9.568-31.2-9.12-40.096z" />
          </svg>
        );
      });
    }
  }

  // chart controller - Ordens de Serviço Duvida *****************************************************************************************************************

  setDataGraphicsDuvida(data: any[]): void {
    const kpiGraphics: KpiGraphic[] = [];

    if (data) {
      data.forEach((kpi: any) => {
        if (kpi.labelClassificationVo !== 'Total') {
          const label = String(kpi.labelClassificationVo);
          const amount = String(kpi.amountClassificationVo).includes('/') ? String(kpi.amountClassificationVo).split('/')[0] : kpi.amountClassificationVo;
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);

          kpiGraphics.push({
            date: `${String(monthNames[date.getMonth()])}`,
            OS_Total: Number(amount),
            // Tendency: 0, <--Descomentar quando o cálculo da tendencia estiver implementado
          });
        }
      });
    }

    this.graphics = kpiGraphics;
    this.composedChartDuvida.setData(this.graphics);
  }

  @OnMount(OpenedOsAliases.MAIN_COMPOSED_CHART_DUVIDA)
  onChartMountDuvida(event: MountEvent): void {
    const chart = event.target as AxisChartAPI<AxisChartAPIState>;

    chart.setData(this.graphics);
    chart.setLegends({ verticalAlign: ChartAlignment.BOTTOM });
    chart.setXAxis({ dataKey: 'date', height: 50, interval: 'preserveStartEnd' });
    chart.setYAxis({ hide: isMobileResolution(), interval: 'preserveStartEnd', label: 'text.os-duvida' });
  }

  @OnClick(OpenedOsAliases.MAIN_COMPOSED_CHART_DUVIDA)
  onChartClickDuvida(event: ClickEvent): void {
    console.log('Clicked', event);
  }

  @OnDynamicContentRender(OpenedOsAliases.MAIN_COMPOSED_CHART_DUVIDA)
  onLineChartDynamicContentRenderDuvida(event: RenderEvent): void {
    const { render, type } = event;

    if (this.renderCustomDots && type === ChartDynamicRenderType.ACTIVE_DOT) {
      render((dotProps: Record<string, any>) => {
        const { cx, cy, fill } = dotProps;

        return (
          <svg fill={fill} height={20} viewBox="0 0 1024 1024" width={20} x={cx - 10} y={cy - 10}>
            <path d="M512 1009.984c-274.912 0-497.76-222.848-497.76-497.76s222.848-497.76 497.76-497.76c274.912 0 497.76 222.848 497.76 497.76s-222.848 497.76-497.76 497.76zM340.768 295.936c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM686.176 296.704c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM772.928 555.392c-18.752-8.864-40.928-0.576-49.632 18.528-40.224 88.576-120.256 143.552-208.832 143.552-85.952 0-164.864-52.64-205.952-137.376-9.184-18.912-31.648-26.592-50.08-17.28-18.464 9.408-21.216 21.472-15.936 32.64 52.8 111.424 155.232 186.784 269.76 186.784 117.984 0 217.12-70.944 269.76-186.784 8.672-19.136 9.568-31.2-9.12-40.096z" />
          </svg>
        );
      });
    }
  }

  // chart controller - Ordens de Serviço Solicitação *****************************************************************************************************************

  setDataGraphicsSolicitacao(data: any[]): void {
    const kpiGraphics: KpiGraphic[] = [];

    if (data) {
      data.forEach((kpi: any) => {
        if (kpi.labelClassificationVo !== 'Total') {
          const label = String(kpi.labelClassificationVo);
          const amount = String(kpi.amountClassificationVo).includes('/') ? String(kpi.amountClassificationVo).split('/')[0] : kpi.amountClassificationVo;
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);

          kpiGraphics.push({
            date: `${String(monthNames[date.getMonth()])}`,
            OS_Total: Number(amount),
            // Tendency: 0, <--Descomentar quando o cálculo da tendencia estiver implementado
          });
        }
      });
    }

    this.graphics = kpiGraphics;
    this.composedChartSolicitacao.setData(this.graphics);
  }

  @OnMount(OpenedOsAliases.MAIN_COMPOSED_CHART_SOLICITACAO)
  onChartMountSolicitacao(event: MountEvent): void {
    const chart = event.target as AxisChartAPI<AxisChartAPIState>;

    chart.setData(this.graphics);
    chart.setLegends({ verticalAlign: ChartAlignment.BOTTOM });
    chart.setXAxis({ dataKey: 'date', height: 50, interval: 'preserveStartEnd' });
    chart.setYAxis({ hide: isMobileResolution(), interval: 'preserveStartEnd', label: 'text.os-solicitacao' });
  }

  @OnClick(OpenedOsAliases.MAIN_COMPOSED_CHART_SOLICITACAO)
  onChartClickSolicitacao(event: ClickEvent): void {
    console.log('Clicked', event);
  }

  @OnDynamicContentRender(OpenedOsAliases.MAIN_COMPOSED_CHART_SOLICITACAO)
  onLineChartDynamicContentSolicitacao(event: RenderEvent): void {
    const { render, type } = event;

    if (this.renderCustomDots && type === ChartDynamicRenderType.ACTIVE_DOT) {
      render((dotProps: Record<string, any>) => {
        const { cx, cy, fill } = dotProps;

        return (
          <svg fill={fill} height={20} viewBox="0 0 1024 1024" width={20} x={cx - 10} y={cy - 10}>
            <path d="M512 1009.984c-274.912 0-497.76-222.848-497.76-497.76s222.848-497.76 497.76-497.76c274.912 0 497.76 222.848 497.76 497.76s-222.848 497.76-497.76 497.76zM340.768 295.936c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM686.176 296.704c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM772.928 555.392c-18.752-8.864-40.928-0.576-49.632 18.528-40.224 88.576-120.256 143.552-208.832 143.552-85.952 0-164.864-52.64-205.952-137.376-9.184-18.912-31.648-26.592-50.08-17.28-18.464 9.408-21.216 21.472-15.936 32.64 52.8 111.424 155.232 186.784 269.76 186.784 117.984 0 217.12-70.944 269.76-186.784 8.672-19.136 9.568-31.2-9.12-40.096z" />
          </svg>
        );
      });
    }
  }

  // chart controller - Ordens de Serviço Sugestão *****************************************************************************************************************

  setDataGraphicsSugestao(data: any[]): void {
    const kpiGraphics: KpiGraphic[] = [];

    if (data) {
      data.forEach((kpi: any) => {
        if (kpi.labelClassificationVo !== 'Total') {
          const label = String(kpi.labelClassificationVo);
          const amount = String(kpi.amountClassificationVo).includes('/') ? String(kpi.amountClassificationVo).split('/')[0] : kpi.amountClassificationVo;
          const date = new Date(`${String(label.split('-')[0])}/${1}/${String(label.split('-')[1])}`);

          kpiGraphics.push({
            date: `${String(monthNames[date.getMonth()])}`,
            OS_Total: Number(amount),
            // Tendency: 0, <--Descomentar quando o cálculo da tendencia estiver implementado
          });
        }
      });
    }

    this.graphics = kpiGraphics;
    this.composedChartSugestao.setData(this.graphics);
  }

  @OnMount(OpenedOsAliases.MAIN_COMPOSED_CHART_SUGESTAO)
  onChartMountSugestao(event: MountEvent): void {
    const chart = event.target as AxisChartAPI<AxisChartAPIState>;

    chart.setData(this.graphics);
    chart.setLegends({ verticalAlign: ChartAlignment.BOTTOM });
    chart.setXAxis({ dataKey: 'date', height: 50, interval: 'preserveStartEnd' });
    chart.setYAxis({ hide: isMobileResolution(), interval: 'preserveStartEnd', label: 'text.os-sugestao' });
  }

  @OnClick(OpenedOsAliases.MAIN_COMPOSED_CHART_SUGESTAO)
  onChartClickSugestao(event: ClickEvent): void {
    console.log('Clicked', event);
  }

  @OnDynamicContentRender(OpenedOsAliases.MAIN_COMPOSED_CHART_SUGESTAO)
  onLineChartDynamicContentSugestao(event: RenderEvent): void {
    const { render, type } = event;

    if (this.renderCustomDots && type === ChartDynamicRenderType.ACTIVE_DOT) {
      render((dotProps: Record<string, any>) => {
        const { cx, cy, fill } = dotProps;

        return (
          <svg fill={fill} height={20} viewBox="0 0 1024 1024" width={20} x={cx - 10} y={cy - 10}>
            <path d="M512 1009.984c-274.912 0-497.76-222.848-497.76-497.76s222.848-497.76 497.76-497.76c274.912 0 497.76 222.848 497.76 497.76s-222.848 497.76-497.76 497.76zM340.768 295.936c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM686.176 296.704c-39.488 0-71.52 32.8-71.52 73.248s32.032 73.248 71.52 73.248c39.488 0 71.52-32.8 71.52-73.248s-32.032-73.248-71.52-73.248zM772.928 555.392c-18.752-8.864-40.928-0.576-49.632 18.528-40.224 88.576-120.256 143.552-208.832 143.552-85.952 0-164.864-52.64-205.952-137.376-9.184-18.912-31.648-26.592-50.08-17.28-18.464 9.408-21.216 21.472-15.936 32.64 52.8 111.424 155.232 186.784 269.76 186.784 117.984 0 217.12-70.944 269.76-186.784 8.672-19.136 9.568-31.2-9.12-40.096z" />
          </svg>
        );
      });
    }
  }

}
